import { useEffect, useRef } from 'react';
import { useTabsStore } from '../../store/tabsStore';
import { useDocsLayoutStore, DOCS_BREAKPOINTS } from './docsLayoutStore';
import { useMediaQuery } from './useMediaQuery';
import './docsLayout.css';

/**
 * Below 900 px the Documents sidebar collapses and opens as an overlay from
 * the document toolbar (card 2.6; reference: VS Code's secondary sidebar,
 * GitHub's mobile file tree). For AppContent's sidebar column:
 *
 * returns `{ overlay, open, wrapRef }` — `overlay` while the documents panel
 * is narrow, `open` while the overlay shows. Escape, a click outside, opening
 * a document, or widening the window closes it.
 */
export function useDocsSidebarOverlay(activeTab) {
  const narrow = !useMediaQuery(`(min-width: ${DOCS_BREAKPOINTS.sidebar}px)`);
  const overlay = activeTab === 'documents.panel' && narrow;
  const open = useDocsLayoutStore((s) => s.overlayOpen);
  const setOpen = useDocsLayoutStore((s) => s.setOverlayOpen);
  const activeDocumentTabId = useTabsStore((s) => s.activeDocumentTabId);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!overlay && open) setOpen(false);
  }, [overlay, open, setOpen]);

  useEffect(() => {
    setOpen(false);
  }, [activeDocumentTabId, setOpen]);

  useEffect(() => {
    if (!overlay || !open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      // The toolbar's own toggle handles itself.
      if (e.target.closest?.('[data-docs-sidebar-toggle]')) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [overlay, open, setOpen]);

  return { overlay, open: overlay && open, wrapRef };
}
